import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PASSWORD_MIN_LENGTH, type RegistrationInfo } from '@lizrd/contracts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordRequirements, isPasswordValid } from '../components/auth/PasswordRequirements';
import { SsoButtons } from '../components/auth/SsoButtons';
import { AuthValueRail } from '../components/auth/AuthValueRail';
import { RegionBanner, useRegionRouting } from '../components/auth/RegionChooser';
import { useAuthStore } from '../store/auth';
import * as api from '../lib/api';

/** Legal + marketing pages live on the marketing site (www.lizrd.ai), not the app. */
const LEGAL = {
  terms: 'https://www.lizrd.ai/terms',
  privacy: 'https://www.lizrd.ai/privacy',
} as const;
// The marketing site lives on a different host per environment (staging.lizrd.ai
// vs www.lizrd.ai), so the "request access" link is build-time configurable.
const WEBSITE_BASE_URL = import.meta.env.VITE_WEBSITE_BASE_URL ?? 'https://www.lizrd.ai';
const REQUEST_ACCESS_URL = `${WEBSITE_BASE_URL}/beta`;

/** Which register experience to show. During the private beta, self-serve signup
 * is invite-only (Feature 27): a valid emailed token unlocks a locked-email form;
 * anyone else is pointed at the waitlist. Outside beta it's the open form. */
type Mode = 'loading' | 'open' | 'invited' | 'invite-only';

export function RegisterPage() {
  const register = useAuthStore((s) => s.register);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  const [mode, setMode] = useState<Mode>('loading');
  const [regInfo, setRegInfo] = useState<RegistrationInfo | null>(null);
  const [form, setForm] = useState({ organizationName: '', name: '', email: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Data residency: settle on the right regional host + expose a region switch.
  const { redirecting, changeRegion } = useRegionRouting(regInfo);

  // Resolve the registration mode (and validate any invite token) on mount.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const info = await api.getRegistrationInfo();
        if (!active) return;
        setRegInfo(info);
        if (!info.betaInviteRequired) {
          setMode('open');
          return;
        }
        if (!inviteToken) {
          setMode('invite-only');
          return;
        }
        const check = await api.validateBetaInvite(inviteToken);
        if (!active) return;
        if (check.valid && check.email) {
          setForm((f) => ({ ...f, email: check.email as string }));
          setMode('invited');
        } else {
          setMode('invite-only');
        }
      } catch {
        // If we can't reach the mode endpoint, fail open to the standard form
        // rather than blocking signup on a transient error.
        if (active) setMode('open');
      }
    })();
    return () => {
      active = false;
    };
  }, [inviteToken]);

  function update(field: keyof typeof form) {
    return (e: { target: { value: string } }) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await register({ ...form, invite: inviteToken ?? undefined });
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setBusy(false);
    }
  }

  const emailLocked = mode === 'invited';

  return (
    <div className="grid overflow-hidden rounded-lg border border-border bg-surface shadow-card lg:grid-cols-2">
      {/* Left rail — why it's worth the two minutes. */}
      <aside className="hidden flex-col justify-between gap-8 bg-surface-2 p-8 lg:flex">
        <div className="space-y-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-iris">
            {mode === 'invited' ? 'Private beta' : 'Start free'}
          </p>
          <h2 className="font-display text-2xl font-bold leading-tight text-bone">
            Turn cloud waste into savings you can prove.
          </h2>
          <p className="text-sm leading-relaxed text-muted">
            Complete transparency into what your infrastructure really costs — plus the savings, the
            exact fix, and proof of the money back.
          </p>
        </div>

        <AuthValueRail />
      </aside>

      {/* Right — the form (or the invite-only notice). */}
      <div className="p-8">
        {(mode === 'loading' || redirecting) && (
          <p className="text-sm text-muted">
            {redirecting ? 'Taking you to your region…' : 'Loading…'}
          </p>
        )}

        {!redirecting && mode === 'invite-only' && (
          <div className="space-y-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-iris">
              Private beta
            </p>
            <h1 className="font-display text-xl font-bold text-bone">Lizrd is invite-only</h1>
            <p className="text-sm leading-relaxed text-muted">
              We're onboarding teams in small groups during the private beta. Request access and
              we'll email you a signup link when a spot opens.
            </p>
            <a href={REQUEST_ACCESS_URL} target="_blank" rel="noreferrer" className="block">
              <Button className="w-full">Request beta access</Button>
            </a>
            <p className="text-sm text-muted">
              Already have an account?{' '}
              <Link to="/login" className="text-iris hover:text-iris-strong">
                Sign in
              </Link>
            </p>
          </div>
        )}

        {!redirecting && (mode === 'open' || mode === 'invited') && (
          <>
            <h1 className="font-display text-xl font-bold text-bone">
              {mode === 'invited' ? "You're invited — create your account" : 'Create your account'}
            </h1>
            <p className="mt-1 text-sm text-muted">
              {mode === 'invited'
                ? 'Set a password to finish setting up your workspace.'
                : 'Set up your workspace and start optimizing in minutes.'}
            </p>

            {regInfo && (
              <div className="mt-5">
                <RegionBanner info={regInfo} context="signup" onChange={changeRegion} />
              </div>
            )}

            <div className="mt-6">
              <SsoButtons />
            </div>

            <div className="my-5 flex items-center gap-3 text-xs text-muted">
              <span className="h-px flex-1 bg-border" />
              or sign up with email
              <span className="h-px flex-1 bg-border" />
            </div>

            <form onSubmit={onSubmit} className="space-y-3">
              {error && <p className="text-sm text-danger">{error}</p>}
              <Input
                type="email"
                required
                placeholder="Work email"
                value={form.email}
                onChange={update('email')}
                readOnly={emailLocked}
                aria-readonly={emailLocked}
                className={emailLocked ? 'cursor-not-allowed opacity-70' : undefined}
              />
              <Input required placeholder="Your name" value={form.name} onChange={update('name')} />
              <Input
                required
                placeholder="Company / workspace name"
                value={form.organizationName}
                onChange={update('organizationName')}
              />
              <Input
                type="password"
                required
                minLength={PASSWORD_MIN_LENGTH}
                placeholder="Password"
                value={form.password}
                onChange={update('password')}
              />
              <PasswordRequirements value={form.password} />
              <Button
                type="submit"
                disabled={busy || !isPasswordValid(form.password)}
                className="w-full"
              >
                {busy ? 'Creating…' : 'Create account'}
              </Button>
            </form>

            <p className="mt-4 text-xs leading-relaxed text-muted">
              By creating an account you agree to our{' '}
              <a
                href={LEGAL.terms}
                target="_blank"
                rel="noreferrer"
                className="text-haze hover:text-bone"
              >
                Terms
              </a>{' '}
              and{' '}
              <a
                href={LEGAL.privacy}
                target="_blank"
                rel="noreferrer"
                className="text-haze hover:text-bone"
              >
                Privacy Policy
              </a>
              .
            </p>
            <p className="mt-4 text-sm text-muted">
              Already have an account?{' '}
              <Link to="/login" className="text-iris hover:text-iris-strong">
                Sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
